import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import { CheckCircle2, PlugZap, RefreshCw, Webhook } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

type MiaolinWebhookKind =
  | 'loginCheck'
  | 'salesUsersSync'
  | 'channels'
  | 'channelTypes'
  | 'teamSalesUsers'
  | 'customerCheck'
  | 'existingCustomerCheck'
  | 'reviewNotification'
  | 'erpCustomerLookup'

type IntegrationSettingContract = {
  kind: MiaolinWebhookKind
  enabled: boolean
}

type WebhookSpec = {
  id: string
  kind: MiaolinWebhookKind
  title: string
  envPrefix: string
  route: string
  method: 'POST' | 'GET'
  timing: string
  purpose: string
  request: Record<string, unknown>
  successResponse: Record<string, unknown>
  errorResponse: Record<string, unknown>
  notes: string[]
}

const webhookSpecs: WebhookSpec[] = [
  {
    id: 'login-check',
    kind: 'loginCheck',
    title: '登入資格檢查',
    envPrefix: 'MIAOLIN_LOGIN_CHECK_WEBHOOK',
    route: '/api/auth/public-form-access、Google / 內部帳密登入後端流程',
    method: 'POST',
    timing:
      '使用者已通過 Google 或帳密登入後，後端再用 email 與角色資訊檢查是否允許進入系統或填寫問卷。',
    purpose: '確認登入者是否有資格使用本系統與公開問卷。',
    request: {
      event: 'miaolin.login_check',
      authFlow: 'google',
      email: 'user@example.test',
      formId: '<formId-or-null>',
      userId: '<userId>',
      workspaceId: '11111111-1111-4111-8111-111111111111',
      productionRole: 'admin',
      businessRole: 'admin',
      permissionScope: 'workspace:admin',
    },
    successResponse: {
      status: 'success',
      message: '允許登入。',
      data: { allowed: true },
    },
    errorResponse: {
      status: 'error',
      message: '此帳號目前不可使用系統。',
      data: { allowed: false, reason: 'not_allowed' },
    },
    notes: [
      '沒有設定 URL 時不呼叫外部 webhook，登入資格由 D1 帳戶清單與既有邏輯判斷。',
      'GET 模式會把同樣欄位放在 query string；預設建議 POST JSON。',
    ],
  },
  {
    id: 'channels',
    kind: 'channels',
    title: '客戶通路別選項',
    envPrefix: 'MIAOLIN_CHANNELS_WEBHOOK',
    route: '/api/public/forms/:formId',
    method: 'POST',
    timing:
      '填寫問卷載入時，後端呼叫 webhook 取得「客戶通路別」選項；失敗時使用 D1 問卷內的預設選項。',
    purpose: '讓通路別選項可由 n8n / ERP API 更新。',
    request: {
      event: 'miaolin.channel_options_sync',
      formId: '<formId>',
      questionId: 'q-miaolin-33',
      requestedAt: '<iso-timestamp>',
    },
    successResponse: {
      status: 'success',
      message: 'success',
      data: [{ channel: '餐飲' }, { channel: '烘焙業' }],
    },
    errorResponse: {
      status: 'error',
      message: '通路別暫時無法更新。',
      data: { reason: 'provider_unavailable' },
    },
    notes: [
      '回應 data 可以是陣列，也可以包在 data.channels / data.options。',
      '每個選項會正規化成 label/value；不輸出 raw provider payload 到 logs。',
    ],
  },
  {
    id: 'channel-types',
    kind: 'channelTypes',
    title: '型態別選項',
    envPrefix: 'MIAOLIN_CHANNEL_TYPES_WEBHOOK',
    route: '/api/public/forms/:formId/miaolin-channel-types',
    method: 'POST',
    timing:
      '填寫者點選「客戶通路別」後，後端以 channel 取得「型態別」選項；失敗時使用 D1 問卷預設對應。',
    purpose: '依通路別動態更新單一「型態別」題目的選項。',
    request: {
      event: 'miaolin.channel_type_options_sync',
      formId: '<formId>',
      questionId: 'q-miaolin-34',
      channel: '烘焙業',
      channelValue: '烘焙業',
      requestedAt: '<iso-timestamp>',
    },
    successResponse: {
      status: 'success',
      message: 'success',
      data: [
        { type_no: '230', type: '個人工作室', customer_count: 2627 },
        { type_no: '212', type: '西式甜點店', customer_count: 2212 },
      ],
    },
    errorResponse: {
      status: 'error',
      message: '型態別暫時無法更新。',
      data: { reason: 'provider_unavailable' },
    },
    notes: ['目前顯示格式會合併 type_no 與 type，例如「230 個人工作室」。'],
  },
  {
    id: 'team-sales-users',
    kind: 'teamSalesUsers',
    title: '指定部門業務選項',
    envPrefix: 'MIAOLIN_TEAM_SALES_USERS_WEBHOOK',
    route: '/api/public/forms/:formId/miaolin-team-sales-users',
    method: 'POST',
    timing: '保留為 server-side future provider 設定；目前問卷填寫時優先讀 D1 業務清單的部門代號。',
    purpose: '未來可依 team_code 從外部系統取得該部門業務名單。',
    request: {
      event: 'miaolin.team_sales_users_sync',
      formId: '<formId>',
      questionId: '<sales-question-id>',
      team_code: '104',
      requestedAt: '<iso-timestamp>',
    },
    successResponse: {
      status: 'success',
      message: '已取得資訊。',
      data: {
        users: [
          {
            name: '王小明',
            employeeId: 'M214',
            team: '苗實 104',
            team_code: 104,
            title: '業務',
            email: 'sales@example.test',
          },
        ],
      },
    },
    errorResponse: {
      status: 'error',
      message: '業務名單暫時無法取得。',
      data: { reason: 'provider_unavailable' },
    },
    notes: ['目前正式流程不主動呼叫此 webhook；D1 找不到資料時回到問卷既有選項。'],
  },
  {
    id: 'customer-check',
    kind: 'customerCheck',
    title: '全新客戶重複檢查',
    envPrefix: 'MIAOLIN_CUSTOMER_CHECK_WEBHOOK',
    route: '/api/public/customer-duplicate-check',
    method: 'POST',
    timing: '全新客戶路線中，使用者按「比對」或送出前 server-side duplicate revalidation 時呼叫。',
    purpose: '檢查客戶代號、客戶簡稱或統一編號是否可能和外部資料重複。',
    request: {
      event: 'miaolin.customer_duplicate_check',
      purpose: 'duplicate_check',
      field: 'customer_short_name',
      fieldLabel: '客戶簡稱',
      value: 'B2B測試',
      formId: '<formId>',
      questionId: '<questionId>',
      requestedAt: '<iso-timestamp>',
    },
    successResponse: {
      status: 'success',
      message: '未找到重複客戶代號。',
      data: { available: true, status: 'not_found' },
    },
    errorResponse: {
      status: 'error',
      message: '外部系統暫時無法完成客戶比對，請稍後再試。',
      data: { reason: 'provider_unavailable' },
    },
    notes: [
      '不要回 matchedCustomerId 或 matchedCustomerName。',
      '既有客戶代號 Q3 不使用這個 webhook；會改用 MIAOLIN_EXISTING_CUSTOMER_CHECK_WEBHOOK。',
    ],
  },
  {
    id: 'existing-customer-check',
    kind: 'existingCustomerCheck',
    title: '既有客戶代號檢查',
    envPrefix: 'MIAOLIN_EXISTING_CUSTOMER_CHECK_WEBHOOK',
    route: '/api/public/customer-duplicate-check',
    method: 'POST',
    timing: '選擇「既有客戶變更資料」後，Q3「既有客戶代號」按比對與送出前 revalidation 時呼叫。',
    purpose: '確認既有客戶代號存在；這和全新客戶重複檢查是不同 webhook。',
    request: {
      event: 'miaolin.customer_existing_lookup',
      purpose: 'existing_lookup',
      field: 'existing_customer_code',
      fieldLabel: '既有客戶代號',
      value: 'A0000',
      formId: '<formId>',
      questionId: 'q-miaolin-03',
      requestedAt: '<iso-timestamp>',
    },
    successResponse: {
      status: 'success',
      message: '既有客戶代號已確認存在。',
      data: { exists: true, status: 'found' },
    },
    errorResponse: {
      status: 'error',
      message: '既有客戶代號暫時無法確認，請稍後再試。',
      data: { reason: 'provider_unavailable' },
    },
    notes: [
      'Headers 會包含 Accept、X-MiaoForm-Correlation-Id、X-MiaoForm-Environment；若設定 bearer token，會加 Authorization。',
      'GET 模式會把 event、purpose、field、fieldLabel、value、formId、questionId、requestedAt 放在 query string。',
    ],
  },
  {
    id: 'erp-customer-lookup',
    kind: 'erpCustomerLookup',
    title: 'ERP 客戶資料查詢',
    envPrefix: 'MIAOLIN_ERP_CUSTOMER_LOOKUP_WEBHOOK',
    route: '/api/customers/:customerId/erp-lookup',
    method: 'POST',
    timing: '客戶管理的 ERP 建檔頁輸入 ERP 客戶代號並點擊「比對」時呼叫。',
    purpose: '取得 ERP 客戶資料，讓管理者選擇欄位更新到目前客戶資料。',
    request: {
      event: 'miaolin.erp_customer_lookup',
      customerId: '<customerId>',
      erpCustomerCode: 'A1104',
    },
    successResponse: {
      status: 'success',
      message: 'ERP 客戶資料已取得。',
      data: {
        customer: {
          customer_code: 'A1104',
          customer_name: 'Smith & Hsu(御匯)',
          channel: '飲品咖啡店',
          tax_id: '28454410',
        },
      },
    },
    errorResponse: {
      status: 'error',
      message: 'ERP 客戶資料暫時無法取得。',
      data: { reason: 'provider_unavailable' },
    },
    notes: ['回應 customer 只會被白名單欄位正規化，不保存 raw provider payload。'],
  },
  {
    id: 'review-notification',
    kind: 'reviewNotification',
    title: '審核通知發送',
    envPrefix: 'MIAOLIN_REVIEW_NOTIFICATION_WEBHOOK',
    route: 'public submit / review notification job sender',
    method: 'POST',
    timing:
      '問卷送出建立待審核記錄後，建立通知 job 並呼叫 webhook；失敗會留在審核通知管理可再次發送。',
    purpose: '由 n8n 發送審核通知 Email。',
    request: {
      event: 'miaolin.review_notification',
      applicationId: '<applicationId>',
      formId: '<formId>',
      form_type: '全新客戶',
      customer_short_name: 'B2B測試',
      submitted_by: '填單人員',
      salesManagerName: '主管姓名',
      reviewerEmail: 'reviewer@example.test',
      reviewerEmails: ['reviewer@example.test'],
      detailLink: '<direct-detail-link>',
      approvalLink: '<direct-approve-link>',
      emailSubject: '新客戶申請待審核：B2B測試',
      emailBody: '<html-email-body>',
      correlationId: '<correlationId>',
    },
    successResponse: {
      status: 'success',
      message: '審核通知已送出。',
      data: { status: 'accepted' },
    },
    errorResponse: {
      status: 'error',
      message: '審核通知暫時無法送出。',
      data: { reason: 'provider_unavailable' },
    },
    notes: ['emailBody 只傳給 webhook，不寫入 raw logs；前端不顯示 raw recipient 或 raw links。'],
  },
  {
    id: 'sales-users-sync',
    kind: 'salesUsersSync',
    title: '業務清單同步',
    envPrefix: 'MIAOLIN_SALES_USERS_SYNC_WEBHOOK',
    route: '/api/sales-users/sync',
    method: 'POST',
    timing: '管理員在業務清單點擊「同步業務」時才呼叫；不會自動同步。',
    purpose: '從 n8n / ERP 取得業務、主管、業務助理與一般管理員清單，新增或補齊 D1 欄位。',
    request: {
      event: 'miaolin.sales_users_sync',
      authFlow: 'manual',
      email: 'admin@example.test',
      workspaceId: '11111111-1111-4111-8111-111111111111',
      requestedAt: '<iso-timestamp>',
      correlationId: '<correlationId>',
      page: 1,
      pageSize: 100,
      cursor: '<optional-cursor>',
    },
    successResponse: {
      status: 'success',
      message: '已取得資訊。',
      data: {
        users: [
          {
            name: '王小明',
            employeeId: 'M214',
            location: '台北8樓',
            department: '業務部',
            team: '苗實 104',
            title: '業務',
            email: 'sales@example.test',
            directManager: '林主管',
            team_code: 104,
          },
        ],
      },
    },
    errorResponse: {
      status: 'error',
      message: '業務清單暫時無法同步。',
      data: { reason: 'provider_unavailable' },
    },
    notes: [
      '同步時不會使用 n8n 回傳的 status、is_active 或 isActive 更新啟用/停用。',
      '已存在欄位不覆蓋；空白欄位才用 ERP snapshot 補齊。',
    ],
  },
]

function createDefaultEnabledState() {
  return Object.fromEntries(webhookSpecs.map((spec) => [spec.id, true])) as Record<string, boolean>
}

function createEnabledStateFromSettings(settings: IntegrationSettingContract[]) {
  const defaults = Object.fromEntries(webhookSpecs.map((spec) => [spec.id, true]))
  const settingByKind = new Map(settings.map((setting) => [setting.kind, setting.enabled]))
  return Object.fromEntries(
    webhookSpecs.map((spec) => [spec.id, settingByKind.get(spec.kind) ?? defaults[spec.id]]),
  ) as Record<string, boolean>
}

async function readIntegrationSettingsFromApi() {
  if (typeof fetch !== 'function') return []
  const response = await fetch('/api/system/integration-settings', {
    headers: { Accept: 'application/json' },
    credentials: 'include',
  })
  if (!response.ok) throw new Error('integration_settings_load_failed')
  const payload = (await response.json()) as {
    data?: { settings?: IntegrationSettingContract[] }
  }
  return Array.isArray(payload.data?.settings) ? payload.data.settings : []
}

async function updateIntegrationSettingToApi(kind: MiaolinWebhookKind, enabled: boolean) {
  const response = await fetch(`/api/system/integration-settings/${kind}`, {
    method: 'PATCH',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ enabled }),
  })
  if (!response.ok) throw new Error('integration_settings_save_failed')
  const payload = (await response.json()) as {
    data?: { setting?: IntegrationSettingContract | null }
  }
  return payload.data?.setting ?? { kind, enabled }
}

function formatJson(value: Record<string, unknown>) {
  return JSON.stringify(value, null, 2)
}

function CodeBlock({ label, value }: { label: string; value: Record<string, unknown> }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </Label>
      <pre className="max-h-80 overflow-auto rounded-md border border-slate-200 bg-slate-50 p-3 text-xs leading-relaxed text-slate-900">
        <code>{formatJson(value)}</code>
      </pre>
    </div>
  )
}

export default function IntegrationsRoute() {
  const [selectedId, setSelectedId] = useState(webhookSpecs[0]?.id ?? '')
  const [enabledState, setEnabledState] = useState<Record<string, boolean>>(() =>
    createDefaultEnabledState(),
  )
  const [settingsMessage, setSettingsMessage] = useState<string | null>(null)
  const [settingsError, setSettingsError] = useState<string | null>(null)
  const [settingsLoading, setSettingsLoading] = useState(false)

  useEffect(() => {
    let cancelled = false
    async function loadSettings() {
      setSettingsLoading(true)
      setSettingsError(null)
      try {
        const settings = await readIntegrationSettingsFromApi()
        if (!cancelled) {
          setEnabledState(createEnabledStateFromSettings(settings))
          setSettingsMessage('串接啟用狀態已讀取後端設定。')
        }
      } catch {
        if (!cancelled) {
          setSettingsError('目前無法讀取串接啟用狀態，畫面先以預設啟用顯示。')
        }
      } finally {
        if (!cancelled) setSettingsLoading(false)
      }
    }
    void loadSettings()
    return () => {
      cancelled = true
    }
  }, [])

  const selectedWebhook = useMemo(
    () => webhookSpecs.find((spec) => spec.id === selectedId) ?? webhookSpecs[0],
    [selectedId],
  )

  async function setWebhookEnabled(id: string, enabled: boolean) {
    const spec = webhookSpecs.find((item) => item.id === id)
    if (!spec) return
    const previous = enabledState[id] !== false
    setEnabledState((current) => ({ ...current, [id]: enabled }))
    setSettingsMessage(null)
    setSettingsError(null)
    try {
      await updateIntegrationSettingToApi(spec.kind, enabled)
      setSettingsMessage(`${spec.title}已${enabled ? '啟用' : '停用'}。`)
    } catch {
      setEnabledState((current) => ({ ...current, [id]: previous }))
      setSettingsError('無法儲存串接啟用狀態，請稍後再試。')
    }
  }

  async function resetToggles() {
    const previous = enabledState
    setEnabledState(createDefaultEnabledState())
    setSettingsMessage(null)
    setSettingsError(null)
    try {
      await Promise.all(webhookSpecs.map((spec) => updateIntegrationSettingToApi(spec.kind, true)))
      setSettingsMessage('所有 webhook 已恢復啟用。')
    } catch {
      setEnabledState(previous)
      setSettingsError('無法全部恢復啟用，請稍後再試。')
    }
  }

  return (
    <main className="flex min-h-[calc(100dvh-1px)] flex-col bg-slate-50">
      <header className="border-b border-slate-200 bg-white px-5 py-4 lg:px-8">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="flex items-center gap-2 text-sm font-medium text-[#4a2f21]">
              <PlugZap className="size-4" />
              串接管理
            </div>
            <h1 className="mt-2 text-2xl font-semibold tracking-normal text-slate-950">
              Webhook 與 n8n 串接規格
            </h1>
            <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-600">
              這裡整理目前正式使用的 webhook、呼叫時機、傳入參數與 n8n 回應格式。畫面不顯示
              secret、token、cookie、OAuth code、raw provider payload 或真實個資。
            </p>
          </div>
          <Button variant="outline" onClick={() => void resetToggles()} disabled={settingsLoading}>
            <RefreshCw className="size-4" />
            全部恢復啟用
          </Button>
          {settingsMessage ? (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
              {settingsMessage}
            </div>
          ) : null}
          {settingsError ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              {settingsError}
            </div>
          ) : null}
        </div>
      </header>

      <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[320px_minmax(0,1fr)]">
        <aside className="border-b border-slate-200 bg-white lg:border-b-0 lg:border-r">
          <ScrollArea className="h-[320px] lg:h-[calc(100dvh-137px)]">
            <div className="space-y-2 p-3">
              {webhookSpecs.map((spec) => {
                const selected = selectedWebhook?.id === spec.id
                const enabled = enabledState[spec.id] !== false
                return (
                  <button
                    key={spec.id}
                    type="button"
                    onClick={() => setSelectedId(spec.id)}
                    className={cn(
                      'flex w-full items-start justify-between gap-3 rounded-md border px-3 py-3 text-left transition-colors',
                      selected
                        ? 'border-[#4a2f21] bg-[#4a2f21]/5 text-[#4a2f21]'
                        : 'border-transparent bg-white text-slate-700 hover:bg-slate-50',
                    )}
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-semibold">{spec.title}</span>
                      <span className="mt-1 block truncate text-xs text-slate-500">
                        {spec.envPrefix}
                      </span>
                    </span>
                    <Badge variant={enabled ? 'success' : 'secondary'} className="shrink-0">
                      {enabled ? '啟用' : '停用'}
                    </Badge>
                  </button>
                )
              })}
            </div>
          </ScrollArea>
        </aside>

        <section className="min-w-0 p-4 lg:p-6">
          {selectedWebhook ? (
            <div className="mx-auto flex max-w-6xl flex-col gap-5">
              <Card>
                <CardHeader className="gap-4">
                  <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2 text-xl">
                        <Webhook className="size-5 text-[#4a2f21]" />
                        {selectedWebhook.title}
                      </CardTitle>
                      <p className="mt-2 text-sm leading-6 text-slate-600">
                        {selectedWebhook.purpose}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                      <span className="text-sm font-medium text-slate-700">啟用狀態</span>
                      <Switch
                        checked={enabledState[selectedWebhook.id] !== false}
                        onCheckedChange={(checked) =>
                          void setWebhookEnabled(selectedWebhook.id, checked)
                        }
                        aria-label={`${selectedWebhook.title} 啟用狀態`}
                      />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div className="grid gap-3 md:grid-cols-3">
                    <div className="rounded-md border border-slate-200 bg-white p-3">
                      <div className="text-xs font-medium text-slate-500">Env prefix</div>
                      <div className="mt-1 break-all text-sm font-semibold text-slate-950">
                        {selectedWebhook.envPrefix}
                      </div>
                    </div>
                    <div className="rounded-md border border-slate-200 bg-white p-3">
                      <div className="text-xs font-medium text-slate-500">預設 Method</div>
                      <div className="mt-1 text-sm font-semibold text-slate-950">
                        {selectedWebhook.method}
                      </div>
                    </div>
                    <div className="rounded-md border border-slate-200 bg-white p-3">
                      <div className="text-xs font-medium text-slate-500">後端路由 / 流程</div>
                      <div className="mt-1 break-all text-sm font-semibold text-slate-950">
                        {selectedWebhook.route}
                      </div>
                    </div>
                  </div>

                  <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
                    <div className="text-sm font-semibold text-slate-950">呼叫時機</div>
                    <p className="mt-1 text-sm leading-6 text-slate-600">
                      {selectedWebhook.timing}
                    </p>
                  </div>

                  <div className="grid gap-4 xl:grid-cols-3">
                    <CodeBlock label="傳入參數 JSON" value={selectedWebhook.request} />
                    <CodeBlock label="成功回應 JSON" value={selectedWebhook.successResponse} />
                    <CodeBlock label="錯誤回應 JSON" value={selectedWebhook.errorResponse} />
                  </div>

                  <div className="rounded-md border border-slate-200 bg-white p-4">
                    <div className="flex items-center gap-2 text-sm font-semibold text-slate-950">
                      <CheckCircle2 className="size-4 text-[#4a2f21]" />
                      注意事項
                    </div>
                    <ul className="mt-3 space-y-2 text-sm leading-6 text-slate-600">
                      {selectedWebhook.notes.map((note) => (
                        <li key={note} className="flex gap-2">
                          <span className="mt-2 size-1.5 shrink-0 rounded-full bg-[#4a2f21]" />
                          <span>{note}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">共同呼叫規則</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-3 text-sm leading-6 text-slate-600 md:grid-cols-2">
                  <div className="rounded-md border border-slate-200 bg-white p-3">
                    Production / Preview / fallback 依序讀取
                    <code className="mx-1 rounded bg-slate-100 px-1 py-0.5">*_URL_PRODUCTION</code>
                    、<code className="mx-1 rounded bg-slate-100 px-1 py-0.5">*_URL_PREVIEW</code>、
                    <code className="mx-1 rounded bg-slate-100 px-1 py-0.5">*_URL</code>。
                  </div>
                  <div className="rounded-md border border-slate-200 bg-white p-3">
                    Method 預設 POST；可用
                    <code className="mx-1 rounded bg-slate-100 px-1 py-0.5">*_METHOD*</code>
                    設為 GET，但仍建議 n8n 使用 POST JSON。
                  </div>
                  <div className="rounded-md border border-slate-200 bg-white p-3">
                    n8n 回應統一使用
                    <code className="mx-1 rounded bg-slate-100 px-1 py-0.5">
                      {'{ status, message, data }'}
                    </code>
                    ；前端可依情境顯示 message。
                  </div>
                  <div className="rounded-md border border-slate-200 bg-white p-3">
                    本頁啟用/停用會保存到後端 KV 設定；停用後，後端 webhook resolver
                    會略過外部呼叫。正式 provider 是否可呼叫仍取決於 Cloudflare env 是否設定
                    URL 與後端 fail-closed 規則。
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : null}
        </section>
      </div>
    </main>
  )
}
